// 📂 frontend/src/components/DashboardStats.tsx
import React, { useState, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';
import emailApi from '../services/emailApi';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface DashboardStatsProps {
  scheduledEmails: any[];
  sentEmails: any[];
  templates: any[];
  users: any[];
  theme: any;
  currentTheme: string;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const DashboardStats: React.FC<DashboardStatsProps> = ({
  scheduledEmails,
  sentEmails,
  templates,
  users,
  theme,
  currentTheme
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month' | 'year'>('week');
  const [stats, setStats] = useState({
    emailsSentToday: 0,
    emailsScheduledToday: 0,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    templateUsage: [] as any[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userActivity: [] as any[],
    emailsByType: {} as Record<string, number>,
    emailsByPriority: {} as Record<string, number>,
    successRate: 0,
    peakHours: [] as number[]
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  // Animație pentru numere
  const AnimatedNumber = ({ value, duration = 1000 }: { value: number; duration?: number }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
      const startTime = Date.now();
      const startValue = displayValue;
      const endValue = value;

      const animate = () => {
        const now = Date.now();
        const progress = Math.min((now - startTime) / duration, 1);
        const currentValue = Math.floor(startValue + (endValue - startValue) * progress);
        
        setDisplayValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }, [value]);

    return <span>{displayValue}</span>;
  };

  // Calculează statistici
  useEffect(() => {
    calculateStats();
  }, [scheduledEmails, sentEmails, templates, selectedPeriod]);

  const calculateStats = () => {
    const now = new Date();
    const periodStart = getPeriodStart(selectedPeriod);

    // Filtrare email-uri pe perioada selectată
    const periodSentEmails = sentEmails.filter(email => 
      new Date(email.scheduledFor) >= periodStart
    );
    const periodScheduledEmails = scheduledEmails.filter(email => 
      new Date(email.scheduledFor) >= periodStart
    );

    // Calculează statistici
    const emailsByType: Record<string, number> = {};
    const emailsByPriority: Record<string, number> = {};
    const hourlyDistribution = new Array(24).fill(0);

    [...periodSentEmails, ...periodScheduledEmails].forEach(email => {
      // By type
      const type = email.emailType || 'transactional';
      emailsByType[type] = (emailsByType[type] || 0) + 1;

      // By priority
      const priority = email.priority || 'normal';
      emailsByPriority[priority] = (emailsByPriority[priority] || 0) + 1;

      // Hourly distribution
      const hour = new Date(email.scheduledFor).getHours();
      hourlyDistribution[hour]++;
    });

    // Template usage
    const templateUsage = templates
      .map(t => ({
        name: t.name,
        usage: t.usageCount,
        percentage: templates.length > 0 ? (t.usageCount / Math.max(...templates.map(t => t.usageCount))) * 100 : 0
      }))
      .sort((a, b) => b.usage - a.usage)
      .slice(0, 5);

    // Success rate
    const totalSent = sentEmails.length;
    const failedEmails = sentEmails.filter(e => e.status === 'failed').length;
    const successRate = totalSent > 0 ? ((totalSent - failedEmails) / totalSent) * 100 : 100;

    setStats({
      emailsSentToday: periodSentEmails.length,
      emailsScheduledToday: periodScheduledEmails.length,
      templateUsage,
      userActivity: [],
      emailsByType,
      emailsByPriority,
      successRate,
      peakHours: hourlyDistribution
    });
  };

  const getPeriodStart = (period: string): Date => {
    const now = new Date();
    switch (period) {
      case 'today':
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
      case 'week':
        const weekAgo = new Date(now);
        weekAgo.setDate(now.getDate() - 7);
        return weekAgo;
      case 'month':
        const monthAgo = new Date(now);
        monthAgo.setMonth(now.getMonth() - 1);
        return monthAgo;
      case 'year':
        const yearAgo = new Date(now);
        yearAgo.setFullYear(now.getFullYear() - 1);
        return yearAgo;
      default:
        return now;
    }
  };

  // Card style cu hover effect
  const getCardStyle = (cardId: string) => ({
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.large,
    padding: '24px',
    marginBottom: '20px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    transform: hoveredCard === cardId ? 'translateY(-4px)' : 'translateY(0)',
    boxShadow: hoveredCard === cardId ? theme.shadows.large : 'none',
    ...(currentTheme === 'futuristic' && hoveredCard === cardId && {
      borderColor: theme.colors.primary + '44',
      boxShadow: `0 0 30px ${theme.colors.primary}33`
    })
  });

  // Grafic simplu pentru distribuția orară
  const HourlyChart = () => {
    const maxValue = Math.max(...stats.peakHours);
    
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', height: '100px', gap: '2px' }}>
        {stats.peakHours.map((value, hour) => (
          <div
            key={hour}
            style={{
              flex: 1,
              height: `${maxValue > 0 ? (value / maxValue) * 100 : 0}%`,
              backgroundColor: hour >= 9 && hour <= 17 ? theme.colors.primary : theme.colors.primary + '66',
              borderRadius: '2px 2px 0 0',
              transition: 'height 0.3s ease',
              position: 'relative',
              minHeight: '2px'
            }}
            title={`${hour}:00 - ${value} email-uri`}
          >
            {value > 0 && (
              <span style={{
                position: 'absolute',
                top: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '10px',
                color: theme.colors.textSecondary,
                display: hour % 3 === 0 ? 'block' : 'none'
              }}>
                {value}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Progress Ring Component
  const ProgressRing = ({ percentage, size = 120, strokeWidth = 8 }: { percentage: number; size?: number; strokeWidth?: number }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={theme.colors.border}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={percentage >= 90 ? theme.colors.success : percentage >= 70 ? theme.colors.warning : theme.colors.danger}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: 'stroke-dashoffset 1s ease',
            strokeLinecap: 'round'
          }}
        />
        <text
          x="50%"
          y="50%"
          dy=".3em"
          textAnchor="middle"
          style={{
            fill: theme.colors.text,
            fontSize: '24px',
            fontWeight: 'bold',
            transform: 'rotate(90deg)',
            transformOrigin: 'center'
          }}
        >
          {Math.round(percentage)}%
        </text>
      </svg>
    );
  };

  // Mini chart pentru tipuri de email-uri
  const EmailTypeChart = () => {
    const types = Object.entries(stats.emailsByType);
    const total = types.reduce((sum, [_, count]) => sum + count, 0);
    
    const typeIcons: Record<string, string> = {
      transactional: '💼',
      marketing: '📢',
      notification: '🔔',
      newsletter: '📰'
    };

    const typeColors: Record<string, string> = {
      transactional: theme.colors.primary,
      marketing: theme.colors.accent,
      notification: theme.colors.warning,
      newsletter: theme.colors.success
    };

    return (
      <div>
        {types.map(([type, count]) => {
          const percentage = total > 0 ? (count / total) * 100 : 0;
          return (
            <div key={type} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '14px' }}>
                  {typeIcons[type] || '📧'} {type.charAt(0).toUpperCase() + type.slice(1)}
                </span>
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  {count} ({percentage.toFixed(0)}%)
                </span>
              </div>
              <div style={{
                height: '8px',
                backgroundColor: theme.colors.background,
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${percentage}%`,
                  backgroundColor: typeColors[type] || theme.colors.primary,
                  transition: 'width 1s ease',
                  borderRadius: '4px'
                }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {/* Header cu selector de perioadă */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h2 style={{ margin: '0 0 8px 0' }}>📊 Dashboard</h2>
          <p style={{ margin: 0, color: theme.colors.textSecondary }}>
            Monitorizează activitatea aplicației tale
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {(['today', 'week', 'month', 'year'] as const).map(period => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              style={{
                padding: '8px 16px',
                backgroundColor: selectedPeriod === period ? theme.colors.primary : 'transparent',
                color: selectedPeriod === period ? '#ffffff' : theme.colors.text,
                border: `1px solid ${selectedPeriod === period ? theme.colors.primary : theme.colors.border}`,
                borderRadius: theme.borderRadius.small,
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
            >
              {period === 'today' ? 'Azi' : period === 'week' ? 'Săptămână' : period === 'month' ? 'Lună' : 'An'}
            </button>
          ))}
        </div>
      </div>

      {/* Carduri principale */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
        {/* Total Email-uri */}
        <div 
          style={getCardStyle('total')}
          onMouseEnter={() => setHoveredCard('total')}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: theme.colors.textSecondary }}>
                Total Email-uri
              </h3>
              <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
                <AnimatedNumber value={scheduledEmails.length + sentEmails.length} />
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                <span style={{ color: theme.colors.success }}>↑ 12%</span> față de perioada anterioară
              </div>
            </div>
            <div style={{ fontSize: '48px', opacity: 0.3 }}>📧</div>
          </div>
        </div>

        {/* Email-uri Programate */}
        <div 
          style={getCardStyle('scheduled')}
          onMouseEnter={() => setHoveredCard('scheduled')}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: theme.colors.textSecondary }}>
                Programate
              </h3>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: theme.colors.warning }}>
                <AnimatedNumber value={stats.emailsScheduledToday} />
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                În așteptare
              </div>
            </div>
            <div style={{ fontSize: '48px', opacity: 0.3 }}>⏳</div>
          </div>
        </div>

        {/* Email-uri Trimise */}
        <div 
          style={getCardStyle('sent')}
          onMouseEnter={() => setHoveredCard('sent')}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: theme.colors.textSecondary }}>
                Trimise
              </h3>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: theme.colors.success }}>
                <AnimatedNumber value={stats.emailsSentToday} />
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                Livrate cu succes
              </div>
            </div>
            <div style={{ fontSize: '48px', opacity: 0.3 }}>✅</div>
          </div>
        </div>

        {/* Template-uri */}
        <div 
          style={getCardStyle('templates')}
          onMouseEnter={() => setHoveredCard('templates')}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: theme.colors.textSecondary }}>
                Template-uri
              </h3>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: theme.colors.accent }}>
                <AnimatedNumber value={templates.length} />
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                Active
              </div>
            </div>
            <div style={{ fontSize: '48px', opacity: 0.3 }}>📝</div>
          </div>
        </div>
      </div>

      {/* Grafice și statistici detaliate */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginTop: '20px' }}>
        {/* Rata de succes */}
        <div style={getCardStyle('success-rate')}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>📈 Rata de Succes</h3>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ProgressRing percentage={stats.successRate} />
          </div>
          <p style={{ textAlign: 'center', marginTop: '16px', color: theme.colors.textSecondary }}>
            Din ultimele {sentEmails.length} email-uri trimise
          </p>
        </div>

        {/* Distribuție pe tipuri */}
        <div style={getCardStyle('email-types')}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>📊 Tipuri de Email-uri</h3>
          <EmailTypeChart />
        </div>

        {/* Activitate orară */}
        <div style={getCardStyle('hourly-activity')}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>⏰ Activitate Orară</h3>
          <HourlyChart />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: theme.colors.textSecondary }}>
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>23:00</span>
          </div>
        </div>

        {/* Top Template-uri */}
        <div style={getCardStyle('top-templates')}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>🏆 Template-uri Populare</h3>
          {stats.templateUsage.map((template, index) => (
            <div key={index} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '14px' }}>
                  {index + 1}. {template.name}
                </span>
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  {template.usage} utilizări
                </span>
              </div>
              <div style={{
                height: '6px',
                backgroundColor: theme.colors.background,
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${template.percentage}%`,
                  backgroundColor: index === 0 ? theme.colors.warning : theme.colors.primary,
                  transition: 'width 1s ease'
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{
        marginTop: '24px',
        padding: '20px',
        backgroundColor: theme.colors.primary + '11',
        borderRadius: theme.borderRadius.large,
        border: `1px solid ${theme.colors.primary}33`
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>⚡ Acțiuni Rapide</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button style={{
            padding: '10px 20px',
            backgroundColor: theme.colors.primary,
            color: '#ffffff',
            border: 'none',
            borderRadius: theme.borderRadius.medium,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📧 Email Nou
          </button>
          <button style={{
            padding: '10px 20px',
            backgroundColor: theme.colors.success,
            color: '#ffffff',
            border: 'none',
            borderRadius: theme.borderRadius.medium,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📝 Template Nou
          </button>
          <button style={{
            padding: '10px 20px',
            backgroundColor: theme.colors.accent,
            color: '#ffffff',
            border: 'none',
            borderRadius: theme.borderRadius.medium,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📊 Export Raport
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;